import { useState, useEffect } from "react";
import { useAuth, API } from "@/App";
import axios from "axios";
import { Moon, Sun, Clock, Star, Plus, Calendar, Loader2, Bed, Coffee, ArrowUpRight, ChevronRight, Zap } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { Progress } from "@/components/ui/progress";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, BarChart, Bar } from "recharts";
import Paywall from "@/components/Paywall";
import { toast } from "sonner";

const QUALITY_LABELS = ["", "Dårlig", "Okay", "God", "Meget god", "Fremragende"];
const QUALITY_COLORS = ["", "#EF4444", "#F59E0B", "#84CC16", "#22C55E", "#10B981"];
const QUALITY_EMOJIS = ["", "😫", "😕", "😐", "😊", "😴"];

export default function SleepTrackerPage() {
  const { authHeaders } = useAuth();
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [logOpen, setLogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  
  const today = new Date().toISOString().split('T')[0];
  const [newLog, setNewLog] = useState({
    date: today,
    hours_slept: 7,
    quality: 3,
    bedtime: "23:00",
    wake_time: "07:00",
    notes: ""
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [logsRes, statsRes] = await Promise.all([
        axios.get(`${API}/sleep-logs?days=30`, { headers: authHeaders() }),
        axios.get(`${API}/sleep-logs/stats`, { headers: authHeaders() })
      ]);
      setLogs(logsRes.data);
      setStats(statsRes.data);
    } catch (err) {
      console.error("Error fetching sleep data:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveLog = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/sleep-logs`, newLog, { headers: authHeaders() });
      toast.success("Søvn logget!");
      setLogOpen(false);
      setNewLog({ ...newLog, date: today, hours_slept: 7, quality: 3, notes: "" });
      fetchData();
    } catch (err) {
      toast.error("Kunne ikke gemme søvnlog");
    } finally {
      setSaving(false);
    }
  };

  const chartData = [...logs].reverse().slice(-14).map(log => ({
    date: new Date(log.date).toLocaleDateString('da-DK', { weekday: 'short', day: 'numeric' }),
    hours: log.hours_slept,
    quality: log.quality
  }));

  const sleepGoal = 8;
  const avgHours = stats?.avg_hours || 0;
  const sleepScore = Math.min(100, Math.round((avgHours / sleepGoal) * 100));

  if (loading) {
    return (
      <Paywall>
        <div className="min-h-screen bg-[#09090b]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 auto-rows-[140px]">
              {[1,2,3,4,5,6,7,8].map(i => (
                <div key={i} className={`bg-[#18181b] border border-white/5 rounded-xl animate-pulse ${i <= 2 ? 'col-span-2 row-span-2' : ''}`} />
              ))}
            </div>
          </div>
        </div>
      </Paywall>
    );
  }

  return (
    <Paywall>
      <div className="min-h-screen bg-[#09090b]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 lg:py-8" data-testid="sleep-tracker-page">
          
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-8 gap-4">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-xl bg-indigo-500/10 flex items-center justify-center">
                <Moon className="h-7 w-7 text-indigo-400" />
              </div>
              <div>
                <h1 style={{ fontFamily: 'Oswald, sans-serif' }} className="font-bold text-2xl sm:text-3xl uppercase tracking-tight text-white">
                  Søvn Tracker
                </h1>
                <p className="text-sm text-zinc-500">Optimer din restitution med bedre søvn</p>
              </div>
            </div>
            
            <Dialog open={logOpen} onOpenChange={setLogOpen}>
              <DialogTrigger asChild>
                <Button className="bg-[#DFFF00] text-black hover:bg-[#C8E600] rounded-xl font-bold h-12 px-6" data-testid="log-sleep-btn">
                  <Plus className="h-5 w-5 mr-2" /> Log Søvn
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-[#18181b] border-zinc-800 text-white max-w-md rounded-xl">
                <DialogHeader>
                  <DialogTitle style={{ fontFamily: 'Oswald, sans-serif' }} className="text-xl uppercase flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-indigo-500/10 flex items-center justify-center">
                      <Moon className="h-5 w-5 text-indigo-400" />
                    </div>
                    Registrer Søvn
                  </DialogTitle>
                </DialogHeader>
                
                <div className="space-y-5 mt-4">
                  <div>
                    <Label className="text-xs uppercase tracking-wider text-zinc-500">Dato</Label>
                    <Input
                      type="date"
                      value={newLog.date}
                      onChange={(e) => setNewLog({...newLog, date: e.target.value})}
                      className="bg-zinc-900 border-zinc-700 mt-1.5 rounded-lg h-11"
                      data-testid="sleep-date-input"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-xs uppercase tracking-wider text-zinc-500 flex items-center gap-1.5">
                        <Bed className="h-3.5 w-3.5" /> Sengetid
                      </Label>
                      <Input
                        type="time"
                        value={newLog.bedtime}
                        onChange={(e) => setNewLog({...newLog, bedtime: e.target.value})}
                        className="bg-zinc-900 border-zinc-700 mt-1.5 rounded-lg h-11"
                        data-testid="sleep-bedtime-input"
                      />
                    </div>
                    <div>
                      <Label className="text-xs uppercase tracking-wider text-zinc-500 flex items-center gap-1.5">
                        <Coffee className="h-3.5 w-3.5" /> Vågnetid
                      </Label>
                      <Input
                        type="time"
                        value={newLog.wake_time}
                        onChange={(e) => setNewLog({...newLog, wake_time: e.target.value})}
                        className="bg-zinc-900 border-zinc-700 mt-1.5 rounded-lg h-11"
                        data-testid="sleep-waketime-input"
                      />
                    </div>
                  </div>

                  <div className="bg-zinc-900/50 rounded-xl p-4">
                    <div className="flex justify-between items-center mb-3">
                      <Label className="text-xs uppercase tracking-wider text-zinc-500">Timer sovet</Label>
                      <span style={{ fontFamily: 'JetBrains Mono, monospace' }} className="text-2xl font-bold text-indigo-400">{newLog.hours_slept}t</span>
                    </div>
                    <Slider
                      value={[newLog.hours_slept]}
                      onValueChange={([v]) => setNewLog({...newLog, hours_slept: v})}
                      min={0}
                      max={12}
                      step={0.5}
                      className="py-2"
                      data-testid="sleep-hours-slider"
                    />
                    <div className="flex justify-between text-xs text-zinc-600 mt-2">
                      <span>0t</span>
                      <span>6t</span>
                      <span>12t</span>
                    </div>
                  </div>

                  <div>
                    <div className="flex justify-between mb-3">
                      <Label className="text-xs uppercase tracking-wider text-zinc-500">Søvnkvalitet</Label>
                      <span className="text-sm font-bold" style={{ color: QUALITY_COLORS[newLog.quality] }}>
                        {QUALITY_LABELS[newLog.quality]}
                      </span>
                    </div>
                    <div className="flex gap-2">
                      {[1, 2, 3, 4, 5].map((q) => (
                        <button
                          key={q}
                          onClick={() => setNewLog({...newLog, quality: q})}
                          className={`flex-1 py-3 rounded-xl text-xl transition-all ${
                            newLog.quality === q 
                              ? 'ring-2 ring-offset-2 ring-offset-[#18181b] scale-105' 
                              : 'opacity-50 hover:opacity-75'
                          }`}
                          style={{ 
                            backgroundColor: QUALITY_COLORS[q] + '20',
                            color: QUALITY_COLORS[q],
                            ringColor: QUALITY_COLORS[q]
                          }}
                          data-testid={`sleep-quality-${q}`}
                        >
                          {QUALITY_EMOJIS[q]}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <Label className="text-xs uppercase tracking-wider text-zinc-500">Noter (valgfrit)</Label>
                    <Input
                      value={newLog.notes}
                      onChange={(e) => setNewLog({...newLog, notes: e.target.value})}
                      className="bg-zinc-900 border-zinc-700 mt-1.5 rounded-lg h-11"
                      placeholder="Drømte godt, vågnede udhvilet..."
                      data-testid="sleep-notes-input"
                    />
                  </div>

                  <Button 
                    onClick={handleSaveLog} 
                    disabled={saving}
                    className="w-full bg-indigo-600 hover:bg-indigo-500 font-bold rounded-xl h-12"
                    data-testid="save-sleep-btn"
                  >
                    {saving ? <Loader2 className="h-5 w-5 animate-spin mr-2" /> : <Moon className="h-5 w-5 mr-2" />}
                    Gem Søvnlog
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>

          {/* Bento Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 auto-rows-[140px]">
            
            {/* Sleep Score - Main Card */}
            <div className="col-span-2 row-span-2 bg-gradient-to-br from-[#18181b] to-[#0f0f12] border border-white/5 rounded-xl p-5 relative overflow-hidden" data-testid="sleep-score-card">
              <div className="absolute top-0 right-0 w-40 h-40 bg-indigo-500/5 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
              <div className="relative h-full flex flex-col">
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-10 h-10 rounded-lg bg-indigo-500/10 flex items-center justify-center">
                    <Zap className="h-5 w-5 text-indigo-400" />
                  </div>
                  <div>
                    <p className="text-xs text-zinc-500 uppercase tracking-wider">Søvn Score</p>
                    <p className="text-sm text-white font-medium">Baseret på {stats?.total_logs || 0} nætter</p>
                  </div>
                </div>
                
                <div className="flex-1 flex items-center justify-center">
                  <div className="relative w-36 h-36">
                    <svg className="w-full h-full -rotate-90">
                      <circle cx="72" cy="72" r="64" stroke="#27272a" strokeWidth="8" fill="none" />
                      <circle 
                        cx="72" cy="72" r="64" 
                        stroke="#818cf8" 
                        strokeWidth="8" 
                        fill="none"
                        strokeDasharray={`${(sleepScore / 100) * 402} 402`}
                        strokeLinecap="round"
                        className="transition-all duration-1000"
                      />
                    </svg>
                    <div className="absolute inset-0 flex flex-col items-center justify-center">
                      <span style={{ fontFamily: 'JetBrains Mono' }} className="text-4xl font-bold text-white">{sleepScore}</span>
                      <span className="text-xs text-zinc-500">af 100</span>
                    </div>
                  </div>
                </div>
                
                <div className="flex items-center justify-center gap-4 mt-4 pt-4 border-t border-white/5">
                  <div className="text-center">
                    <p style={{ fontFamily: 'JetBrains Mono' }} className="text-lg font-bold text-white">{stats?.avg_hours || 0}t</p>
                    <p className="text-xs text-zinc-500">gns/nat</p>
                  </div>
                  <div className="w-px h-8 bg-white/10" />
                  <div className="text-center">
                    <p style={{ fontFamily: 'JetBrains Mono' }} className="text-lg font-bold text-white">{stats?.avg_quality || 0}/5</p>
                    <p className="text-xs text-zinc-500">kvalitet</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Main Chart - Hours */}
            <div className="col-span-2 md:col-span-4 row-span-2 bg-[#18181b] border border-white/5 rounded-xl overflow-hidden" data-testid="sleep-hours-chart">
              <div className="p-4 border-b border-white/5 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Moon className="h-5 w-5 text-indigo-400" />
                  <span style={{ fontFamily: 'Oswald' }} className="uppercase text-sm tracking-wider text-white">Timer Sovet</span>
                </div>
                <span className="text-xs text-zinc-500">Seneste 14 dage</span>
              </div>
              <div className="p-4 h-[calc(100%-56px)]">
                {chartData.length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={chartData}>
                      <defs>
                        <linearGradient id="sleepGradient" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#818cf8" stopOpacity={0.3}/>
                          <stop offset="95%" stopColor="#818cf8" stopOpacity={0}/>
                        </linearGradient>
                      </defs>
                      <XAxis dataKey="date" tick={{ fill: '#71717a', fontSize: 11 }} axisLine={false} tickLine={false} />
                      <YAxis tick={{ fill: '#71717a', fontSize: 11 }} axisLine={false} tickLine={false} width={30} domain={[0, 12]} />
                      <Tooltip 
                        contentStyle={{ background: '#18181b', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', color: '#fff' }}
                        formatter={(value) => [`${value} timer`, 'Søvn']}
                      />
                      <Area type="monotone" dataKey="hours" stroke="#818cf8" strokeWidth={2} fillOpacity={1} fill="url(#sleepGradient)" />
                    </AreaChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-full flex flex-col items-center justify-center text-zinc-500">
                    <Moon className="h-12 w-12 mb-3 opacity-20" />
                    <p className="text-sm">Ingen søvndata endnu</p>
                    <Button onClick={() => setLogOpen(true)} variant="outline" className="mt-3 border-zinc-700 rounded-lg" size="sm">
                      Log din første nat
                    </Button>
                  </div>
                )}
              </div>
            </div>

            {/* Stat Card - Average Hours */}
            <div className="col-span-1 bg-gradient-to-br from-indigo-500/10 to-transparent border border-white/5 rounded-xl p-4 hover:border-indigo-500/20 transition-all">
              <Clock className="h-5 w-5 text-indigo-400 mb-3" />
              <p className="text-xs text-zinc-500 uppercase tracking-wider mb-1">Gennemsnit</p>
              <p style={{ fontFamily: 'JetBrains Mono' }} className="text-2xl font-bold text-indigo-400" data-testid="stat-avg-hours">
                {stats?.avg_hours || 0}t
              </p>
              <p className="text-xs text-zinc-500 mt-1">per nat</p>
            </div>

            {/* Stat Card - Quality */}
            <div className="col-span-1 bg-gradient-to-br from-purple-500/10 to-transparent border border-white/5 rounded-xl p-4 hover:border-purple-500/20 transition-all">
              <Star className="h-5 w-5 text-purple-400 mb-3" />
              <p className="text-xs text-zinc-500 uppercase tracking-wider mb-1">Kvalitet</p>
              <p style={{ fontFamily: 'JetBrains Mono' }} className="text-2xl font-bold text-purple-400" data-testid="stat-quality">
                {stats?.avg_quality || 0}/5
              </p>
              <p className="text-xs text-zinc-500 mt-1">gennemsnit</p>
            </div>

            {/* Stat Card - Sleep Debt */}
            <div className="col-span-1 bg-gradient-to-br from-orange-500/10 to-transparent border border-white/5 rounded-xl p-4 hover:border-orange-500/20 transition-all">
              <Sun className="h-5 w-5 text-orange-400 mb-3" />
              <p className="text-xs text-zinc-500 uppercase tracking-wider mb-1">Søvngæld</p>
              <p style={{ fontFamily: 'JetBrains Mono' }} className="text-2xl font-bold text-orange-400" data-testid="stat-sleep-debt">
                {stats?.sleep_debt || 0}t
              </p>
              <p className="text-xs text-zinc-500 mt-1">at indhente</p>
            </div>

            {/* Stat Card - Days Logged */}
            <div className="col-span-1 bg-gradient-to-br from-emerald-500/10 to-transparent border border-white/5 rounded-xl p-4 hover:border-emerald-500/20 transition-all">
              <Calendar className="h-5 w-5 text-emerald-400 mb-3" />
              <p className="text-xs text-zinc-500 uppercase tracking-wider mb-1">Dage Logget</p>
              <p style={{ fontFamily: 'JetBrains Mono' }} className="text-2xl font-bold text-emerald-400" data-testid="stat-days-logged">
                {stats?.total_logs || 0}
              </p>
              <p className="text-xs text-zinc-500 mt-1">nætter</p>
            </div>

            {/* Quality Chart */}
            <div className="col-span-2 md:col-span-4 lg:col-span-6 bg-[#18181b] border border-white/5 rounded-xl overflow-hidden" data-testid="sleep-quality-chart">
              <div className="p-4 border-b border-white/5 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Star className="h-5 w-5 text-[#DFFF00]" />
                  <span style={{ fontFamily: 'Oswald' }} className="uppercase text-sm tracking-wider text-white">Søvnkvalitet</span>
                </div>
                <span className="text-xs text-zinc-500">1-5 skala</span>
              </div>
              <div className="p-4 h-[180px]">
                {chartData.length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={chartData}>
                      <XAxis dataKey="date" tick={{ fill: '#71717a', fontSize: 11 }} axisLine={false} tickLine={false} />
                      <YAxis tick={{ fill: '#71717a', fontSize: 11 }} axisLine={false} tickLine={false} width={30} domain={[0, 5]} />
                      <Tooltip 
                        contentStyle={{ background: '#18181b', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', color: '#fff' }}
                        formatter={(value) => [`${value}/5`, 'Kvalitet']}
                      />
                      <Bar dataKey="quality" fill="#DFFF00" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-full flex items-center justify-center text-zinc-500">
                    <p className="text-sm">Ingen kvalitetsdata endnu</p>
                  </div>
                )}
              </div>
            </div>

            {/* Recent Logs List */}
            <div className="col-span-2 md:col-span-4 lg:col-span-6 bg-[#18181b] border border-white/5 rounded-xl overflow-hidden">
              <div className="p-4 border-b border-white/5 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Bed className="h-5 w-5 text-indigo-400" />
                  <span style={{ fontFamily: 'Oswald' }} className="uppercase text-sm tracking-wider text-white">Seneste Registreringer</span>
                </div>
                <span className="text-xs text-zinc-500">{logs.length} i alt</span>
              </div>
              <div className="p-4">
                {logs.length === 0 ? (
                  <div className="text-center py-8 text-zinc-500">
                    <Moon className="h-12 w-12 mx-auto mb-4 opacity-20" />
                    <p className="text-sm">Ingen søvnlogs endnu</p>
                    <Button onClick={() => setLogOpen(true)} variant="outline" className="mt-4 border-zinc-700 rounded-lg" data-testid="first-log-btn">
                      <Plus className="h-4 w-4 mr-2" /> Log din første nat
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {logs.slice(0, 5).map((log, idx) => (
                      <div key={log.id || log.date} className="flex items-center justify-between p-4 bg-zinc-900/30 rounded-xl hover:bg-zinc-800/30 transition-colors" data-testid={`sleep-log-${idx}`}>
                        <div className="flex items-center gap-4">
                          <div className="w-12 h-12 rounded-xl bg-indigo-500/10 flex items-center justify-center text-xl">
                            {QUALITY_EMOJIS[log.quality] || "😴"}
                          </div>
                          <div>
                            <p className="font-medium text-white">
                              {new Date(log.date).toLocaleDateString('da-DK', { weekday: 'long', day: 'numeric', month: 'short' })}
                            </p>
                            <p className="text-sm text-zinc-500">
                              {log.bedtime && log.wake_time ? `${log.bedtime} - ${log.wake_time}` : 'Ingen tider angivet'}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-6">
                          <div className="text-right hidden sm:block">
                            <p style={{ fontFamily: 'JetBrains Mono' }} className="text-lg font-bold text-white">{log.hours_slept}t</p>
                            <p className="text-xs text-zinc-500">sovet</p>
                          </div>
                          <div className="text-right">
                            <p className="text-sm font-bold" style={{ color: QUALITY_COLORS[log.quality] }}>
                              {QUALITY_LABELS[log.quality]}
                            </p>
                            <p className="text-xs text-zinc-500">{log.quality}/5</p>
                          </div>
                        </div>
                      </div>
                    ))}
                    {logs.length > 5 && (
                      <p className="text-center text-xs text-zinc-500 pt-2">+ {logs.length - 5} flere registreringer</p>
                    )}
                  </div>
                )}
              </div>
            </div>

          </div>
        </div>
      </div>
    </Paywall>
  );
}
